/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    COOKIE_AUTH_ADMIN_FIX
 * Date:         2026-02-11
 * Modified By:  Claude
 *
 * Description:
 * Fixed Admin menu not showing after cookie-based auth migration.
 * Changed isAdmin check from localStorage token to userProfile (fetched from server).
 *
 * Related Screens / Modules:
 * - client/src/components/layout/Navbar.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MOBILE_TABLET_MIGRATION_PHASE2
 * Date:         2026-02-09
 * Modified By:  Claude
 *
 * Description:
 * Added mobile hamburger menu with slide-out drawer navigation.
 * Mobile (< 576px): Hamburger menu + slide-out drawer
 * Tablet (576px - 991px): Collapsed sidebar (60px, icons only)
 * Desktop (>= 992px): Full sidebar with expand/collapse
 *
 * Related Screens / Modules:
 * - client/src/components/layout/Navbar.tsx
 * - client/src/styles/components/_navigation.scss
 */

import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { mainNavItems, MainNavItem } from "../../constants/navigation";
import { logoutUser } from "../../utils/authUtils";
import { getUserProfile } from "../../services/usersService";
import { fetchNotificationCount } from "../../services/notificationsService";
import { User } from "../../interfaces/User";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { usePwaInstall } from "../../hooks/usePwaInstall";
// [2026-07-06] GUEST_ACCESS_PHASE1: guest indicator in the sidebar (BR-08)
import { useIsGuest } from "../../contexts/GuestModeContext";

const EXPANDED_W = 260;
const COLLAPSED_W = 60;
const LS_KEY = "main.navbar.open";

// Environment indicator component
const EnvironmentIndicator = () => {
  const environment = (process.env.REACT_APP_ENVIRONMENT || "PROD").toUpperCase();
  const showIndicator = environment === "DEV" || environment === "TEST";

  if (!showIndicator) return null;

  return (
    <span
      className="uz-navbar__env-indicator u-Label-10"
      style={{
        backgroundColor:
          environment === "DEV" ? "var(--color-status-green)" : "var(--color-status-orange)",
        color: environment === "DEV" ? "var(--color-white)" : "var(--color-black)",
      }}
    >
      {environment}
    </span>
  );
};

export default function Navbar() {
  // [2026-02-09] Mobile: Use responsive hook for device detection
  const { isMobile, isTablet, isDesktop } = useMediaQuery();
  // [2026-07-06] GUEST_ACCESS_PHASE1: is the visitor an anonymous guest? (BR-08)
  const isGuest = useIsGuest();
  // [2026-03-07] PWA: Install prompt for Android devices
  const { canInstall, promptInstall } = usePwaInstall();

  const [open, setOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem(LS_KEY);
    if (saved != null) return JSON.parse(saved);
    return window.innerWidth >= 992; // open on desktop by default
  });

  // [2026-02-09] Mobile: Track drawer open state separately
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  // [2026-02-09] Notifications: Track new notification count for badge
  const [newNotificationCount, setNewNotificationCount] = useState<number>(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const userButtonRef = useRef<HTMLButtonElement>(null);
  const menuElementRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ bottom: number; left: number } | null>(null);

  useEffect(() => localStorage.setItem(LS_KEY, JSON.stringify(open)), [open]);

  // [2026-02-09] Mobile: Close drawer when switching to desktop
  useEffect(() => {
    if (isDesktop) {
      setDrawerOpen(false);
    }
  }, [isDesktop]);


  // [2026-02-09] Mobile: Handle body scroll lock when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  // [2026-02-09] Mobile: Close drawer on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && drawerOpen) {
        setDrawerOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [drawerOpen]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await getUserProfile();
        if (response.data) {
          setUserProfile(response.data);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    fetchUserProfile();
  }, []);

  useEffect(() => {
    const handleProfileSaved = () => {
      getUserProfile()
        .then((response) => {
          if (response.data) setUserProfile(response.data);
        })
        .catch((error) => console.error("Error refetching user profile:", error));
    };
    window.addEventListener("userProfileSaved", handleProfileSaved);
    return () => window.removeEventListener("userProfileSaved", handleProfileSaved);
  }, []);

  // [2026-02-09] Notifications: Fetch notification count
  const refreshNotificationCount = useCallback(async () => {
    try {
      const response = await fetchNotificationCount();
      console.log("[Notifications] Count response:", response);
      if (response.success) {
        setNewNotificationCount(response.data.count);
      }
    } catch (error) {
      // Silently fail - notification badge is not critical
      console.error("Error fetching notification count:", error);
    }
  }, []);

  useEffect(() => {
    refreshNotificationCount();

    // Listen for dismissal events from NotificationsPage
    const handleDismissed = () => {
      setNewNotificationCount(0);
    };
    window.addEventListener("notificationsDismissed", handleDismissed);

    // Refresh count periodically (every 5 minutes)
    const interval = setInterval(refreshNotificationCount, 5 * 60 * 1000);

    return () => {
      window.removeEventListener("notificationsDismissed", handleDismissed);
      clearInterval(interval);
    };
  }, [refreshNotificationCount]);

  // [2026-02-11] cookie-auth fix: Get isAdmin from userProfile instead of localStorage token
  const canSeeAdmin = !!userProfile?.isAdmin;

  const items = useMemo(
    () => mainNavItems.filter((i: MainNavItem) => (i.requireAdmin ? canSeeAdmin : true)),
    [canSeeAdmin],
  );

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState<string>("");

  const getProfileImageSrc = (): string | null => {
    if (!userProfile?.profileImages?.data) return null;
    const img = userProfile.profileImages;
    return img.data.startsWith("data:") ? img.data : `data:${img.contentType};base64,${img.data}`;
  };

  // Handle menu item clicks
  const handleMenuClick = (label: string) => {
    setIsMenuOpen(false); // Close menu after clicking

    switch (label) {
      case "Profile":
        navigate("/setup/reviewuser");
        break;
      case "Company":
        navigate("/setup/reviewcompany");
        break;
      case "Help": {
        // [2026-07-13] help-link: Open the Help Center docs instead of a support email
        // [2026-08-26] help-link: always a new tab, in every environment. Navigating in place
        // discarded the app page state, and a member already reading a sub-topic needs a fresh
        // tab at the docs root rather than having that topic navigated out from under them.
        const DOCS_URL = "https://uavtradezone.com/docs/";
        window.open(DOCS_URL, "_blank", "noopener,noreferrer");
        break;
      }
      case "Install App":
        promptInstall();
        break;
      case "Log out":
        // [2026-02-11] cookie-auth: Call server to clear cookie
        logoutUser().then(() => {
          navigate("/signin");
          window.location.reload();
        });
        break;
      case "Notifications":
        navigate("/notifications");
        break;
      // [2026-07-07] BR-20: quick access to alert profiles from the user menu
      case "Set Alerts":
        navigate("/alerts");
        break;
      default:
        break;
    }
  };

  // Menu items configuration
  // [2026-02-07] Shortened labels: "My Profile" → "Profile", "Company profile" → "Company"
  // [2026-03-07] PWA: Conditionally include "Install App" between Help and Log out
  const menuItems = [
    { label: "Notifications", icon: "/assets/notifications.svg" },
    // [2026-07-07] BR-20: Set Alerts entry, placed under Notifications (gear/setup icon)
    { label: "Set Alerts", icon: "/assets/setting.svg" },
    { label: "Profile", icon: "/assets/profile.svg" },
    { label: "Company", icon: "/assets/company.svg" },
    { label: "Help", icon: "/assets/help.svg" },
    ...(canInstall ? [{ label: "Install App", icon: "/assets/install.svg" }] : []),
    { label: "Log out", icon: "/assets/log_out.svg" },
  ];

  // Calculate menu position based on user button location
  const calculateMenuPosition = () => {
    if (userButtonRef.current) {
      const rect = userButtonRef.current.getBoundingClientRect();
      const margin = 8; // Margin between button and menu
      
      // Position menu above the button
      // bottom is distance from bottom of viewport to top of button + margin
      const bottomPosition = window.innerHeight - rect.top + margin;
      
      // Center horizontally on the button (will use transform: translateX(-50%) to center)
      const leftPosition = rect.left + rect.width / 2;
      
      setMenuPosition({
        bottom: bottomPosition,
        left: leftPosition,
      });
    }
  };

  // Toggle menu
  const toggleMenu = () => {
    setIsMenuOpen((prev) => {
      const newState = !prev;
      if (newState) {
        // Calculate position when opening
        setTimeout(() => calculateMenuPosition(), 0);
        // [2026-02-09] Refresh notification count when menu opens to clear stale badges
        refreshNotificationCount();
      }
      return newState;
    });
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  // Reposition menu on window resize or scroll
  useEffect(() => {
    if (isMenuOpen) {
      const handleReposition = () => {
        calculateMenuPosition();
      };

      window.addEventListener("resize", handleReposition);
      window.addEventListener("scroll", handleReposition, true);

      return () => {
        window.removeEventListener("resize", handleReposition);
        window.removeEventListener("scroll", handleReposition, true);
      };
    }
  }, [isMenuOpen, open]);

  // Recalculate position when navbar expands/collapses
  useEffect(() => {
    if (isMenuOpen) {
      setTimeout(() => calculateMenuPosition(), 0);
    }
  }, [open, isMenuOpen]);

  // [2026-02-13] Maya: Tablet uses hamburger menu (same as mobile) for better content space
  if (isMobile || isTablet) {
    return (
      <>
        {/* Mobile Fixed Top Bar */}
        <header className="uz-navbar-mobile" aria-label="Main Navigation">
          <button
            className="uz-navbar-mobile__hamburger touch-target"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={drawerOpen}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>

          {/* [2026-02-13] Maya: Removed person icon - redundant with hamburger drawer */}
          <NavLink to="/" className="uz-navbar-mobile__logo" aria-label="Home">
            <img src="/assets/logo.svg" alt="UAVZone" />
          </NavLink>
          {/* [2026-07-06] GUEST_ACCESS: guest indicator visible in the mobile top bar (BR-08) —
              otherwise the "Guest" state is only visible after opening the drawer. */}
          {isGuest ? (
            <button
              type="button"
              className="uz-navbar-mobile__guest"
              onClick={() => navigate("/signin")}
              aria-label="Guest — tap to sign in"
            >
              <span className="uz-navbar-mobile__guest-tag">Guest</span>
              <span className="uz-navbar-mobile__guest-cta">Sign in</span>
            </button>
          ) : (
            /* Empty spacer to keep logo centered */
            <div style={{ width: 40 }} />
          )}
        </header>

        {/* Mobile Drawer Overlay */}
        {drawerOpen && (
          <div
            className="uz-navbar-drawer__overlay"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Mobile Drawer */}
        <aside
          ref={drawerRef}
          className={`uz-navbar-drawer ${drawerOpen ? "open" : ""}`}
          aria-label="Navigation menu"
          aria-hidden={!drawerOpen}
        >
          <div className="uz-navbar-drawer__header">
            <NavLink to="/" onClick={() => setDrawerOpen(false)} aria-label="Home">
              <img src="/assets/logo.svg" alt="UAVZone" className="uz-logo-full" />
            </NavLink>
            <button
              className="uz-navbar-drawer__close touch-target"
              onClick={() => setDrawerOpen(false)}
              aria-label="Close navigation menu"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Search in drawer */}
          <div className="uz-navbar-drawer__search">
            <div className="uz-search-pill">
              <input
                type="text"
                placeholder="Search"
                aria-label="Search"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && searchValue.trim()) {
                    navigate(`/search?q=${encodeURIComponent(searchValue.trim())}`);
                    setSearchValue("");
                    setDrawerOpen(false);
                  }
                }}
              />
              <div className="uz-search-icon">
                <img src="/assets/search.svg" alt="" aria-hidden="true" />
              </div>
            </div>
          </div>

          {/* Navigation items */}
          <nav className="uz-navbar-drawer__nav" role="navigation">
            {items.map(({ to, label, icon, end }: MainNavItem) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) => `uz-nav-item ${isActive ? "active" : ""}`}
                onClick={() => setDrawerOpen(false)}
                aria-current={pathname === to ? "page" : undefined}
              >
                {icon && <img src={icon} alt="" aria-hidden className="uz-nav-icon" />}
                <span className="uz-nav-label">{label}</span>
              </NavLink>
            ))}
          </nav>

          {/* [2026-02-13] Maya: User section with name as section title + separators */}
          <div className="uz-navbar-drawer__bottom">
            <div className="uz-navbar__env-wrapper">
              <EnvironmentIndicator />
            </div>

            {userProfile && (
              <div className="uz-navbar-drawer__user-section">
                <div className="uz-navbar-drawer__separator" />
                <div className="uz-navbar-drawer__user-title">
                  <img
                    src={getProfileImageSrc() || "/assets/profile.svg"}
                    alt=""
                    className="uz-navbar__user-avatar"
                  />
                  <span className="uz-navbar__user-name u-P2-16-medium">
                    {userProfile.firstName} {userProfile.lastName}
                  </span>
                </div>
                <div className="uz-navbar-drawer__separator" />

                <div className="uz-navbar-drawer__user-menu">
                  {menuItems.map((item) => (
                    <button
                      key={item.label}
                      className={`uz-navbar-drawer__menu-item touch-list-item ${
                        item.label === "Notifications" && newNotificationCount > 0
                          ? "uz-navbar__user-menu-item--has-badge"
                          : ""
                      }`}
                      onClick={() => {
                        handleMenuClick(item.label);
                        setDrawerOpen(false);
                      }}
                    >
                      <img src={item.icon} alt="" aria-hidden="true" />
                      <span>{item.label}</span>
                      {item.label === "Notifications" && newNotificationCount > 0 && (
                        <span className="notification-badge__dot" aria-label="New notifications" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </aside>
      </>
    );
  }

  // Desktop: Sidebar with expand/collapse toggle
  const effectiveOpen = open;
  const effectiveWidth = effectiveOpen ? EXPANDED_W : COLLAPSED_W;

  return (
    <aside
      className={`uz-navbar ${effectiveOpen ? "" : "collapsed"}`}
      style={{ width: effectiveWidth }}
      aria-label="Main Navigation"
      onMouseEnter={() => !effectiveOpen && setIsHovered(true)}
      onMouseLeave={() => !effectiveOpen && setIsHovered(false)}
    >
      {/* Top: logo / favicon */}
      <div className="uz-navbar__brand">
        {effectiveOpen ? (
          <>
            <NavLink to="/" aria-label="Home">
              <img src="/assets/logo.svg" alt="UAVZone" className="uz-logo-full" />
            </NavLink>
            {/* [2026-02-13] Maya: Show toggle on all non-mobile sizes */}
            <button
              className="uz-navbar__toggle"
              onClick={() => setOpen((v) => !v)}
              aria-pressed={effectiveOpen}
              aria-label="Collapse navbar"
            >
              <img src="/assets/collaps.svg" alt="" aria-hidden="true" />
            </button>
          </>
        ) : (
          // [2026-02-13] Maya: Allow expand on hover for all non-mobile sizes
          <NavLink
            to="/"
            aria-label="Home"
            onClick={(e) => {
              if (isHovered) {
                e.preventDefault();
                setOpen(true);
              }
            }}
            className="uz-navbar__logo-link"
          >
            <img
              src={isHovered ? "/assets/expend.svg" : "/assets/small_logo.svg"}
              alt="UAVZone"
              className={`uz-logo-compact ${isHovered ? "uz-logo-expand" : ""}`}
            />
          </NavLink>
        )}
      </div>

      {/* Search field */}
      <div className="uz-navbar__search">
        <div className="uz-search-pill">
          <input
            type="text"
            placeholder="Search"
            aria-label="Search"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && searchValue.trim()) {
                navigate(`/search?q=${encodeURIComponent(searchValue.trim())}`);
                setSearchValue(""); // Clear search after navigation
              }
            }}
          />
          {/* [2026-02-07] style-extraction: moved inline style to _navigation.scss */}
          <div
            className="uz-search-icon uz-search-icon--clickable"
            onClick={() => {
              if (searchValue.trim()) {
                navigate(`/search?q=${encodeURIComponent(searchValue.trim())}`);
                setSearchValue(""); // Clear search after navigation
              }
            }}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === "Enter" && searchValue.trim()) {
                navigate(`/search?q=${encodeURIComponent(searchValue.trim())}`);
                setSearchValue(""); // Clear search after navigation
              }
            }}
            aria-label="Search"
          >
            <img src="/assets/search.svg" alt="" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* Nav items */}
      <nav className="uz-navbar__nav" role="navigation">
        {items.map(({ to, label, icon, end }: MainNavItem) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              `uz-nav-item ${isActive ? "active" : ""} ${effectiveOpen ? "" : "collapsed"}`
            }
            aria-current={pathname === to ? "page" : undefined}
            title={!effectiveOpen ? label : undefined}
          >
            {icon && <img src={icon} alt="" aria-hidden className="uz-nav-icon" />}
            {effectiveOpen && <span className="uz-nav-label">{label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Environment indicator and user profile section at bottom */}
      <div className="uz-navbar__bottom">
        {/* Environment indicator */}
        {effectiveOpen && (
          <div className="uz-navbar__env-wrapper">
            <EnvironmentIndicator />
          </div>
        )}

        {/* User profile section */}
        {userProfile && (
          <div className="uz-navbar__user-wrapper" ref={menuRef}>
            <button
              ref={userButtonRef}
              className="uz-navbar__user"
              onClick={toggleMenu}
              aria-expanded={isMenuOpen}
              aria-label="User menu"
              type="button"
            >
              {/* [2026-02-09] Notifications: Avatar wrapper with badge indicator */}
              <div className="uz-navbar__user-avatar-wrapper">
                <img
                  src={getProfileImageSrc() || "/assets/profile.svg"}
                  alt={`${userProfile.firstName} ${userProfile.lastName}`}
                  className="uz-navbar__user-avatar"
                />
                {newNotificationCount > 0 && (
                  <span className="uz-navbar__user-badge" aria-label="New notifications" />
                )}
              </div>
              {effectiveOpen && (
                <span className="uz-navbar__user-name">
                  {userProfile.firstName} {userProfile.lastName}
                </span>
              )}
            </button>
            {isMenuOpen && (
              <div
                ref={menuElementRef}
                className={`uz-navbar__user-menu ${!effectiveOpen ? "uz-navbar__user-menu-collapsed" : ""}`}
                role="menu"
                style={
                  effectiveOpen && menuPosition
                    ? {
                        bottom: `${menuPosition.bottom}px`,
                        left: `${menuPosition.left}px`,
                        transform: "translateX(-50%)",
                      }
                    : undefined
                }
              >
                {menuItems.map((item) => (
                  <div
                    key={item.label}
                    className={`uz-navbar__user-menu-item ${
                      item.label === "Notifications" && newNotificationCount > 0
                        ? "uz-navbar__user-menu-item--has-badge"
                        : ""
                    }`}
                    role="menuitem"
                    onClick={() => handleMenuClick(item.label)}
                    style={{ cursor: "pointer" }}
                  >
                    <img
                      src={item.icon}
                      alt=""
                      aria-hidden="true"
                      className="uz-navbar__user-menu-icon"
                    />
                    <span>{item.label}</span>
                    {/* [2026-02-09] Notifications: Show red dot badge for new notifications */}
                    {item.label === "Notifications" && newNotificationCount > 0 && (
                      <span className="notification-badge__dot" aria-label="New notifications" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* [2026-07-06] GUEST_ACCESS_PHASE1: guest indicator + Sign in / Sign up (BR-08) */}
        {isGuest && (
          <div className="uz-navbar__user-wrapper">
            <button
              type="button"
              className="uz-navbar__user uz-navbar__user--guest"
              onClick={() => navigate("/signin")}
              aria-label="Sign in"
            >
              <div className="uz-navbar__user-avatar-wrapper">
                <img src="/assets/profile.svg" alt="Guest" className="uz-navbar__user-avatar" />
              </div>
              {effectiveOpen && <span className="uz-navbar__user-name">Guest</span>}
            </button>
            {effectiveOpen && (
              <div className="uz-navbar__guest-actions">
                {/* [2026-07-20] guest-align: reuse shared Figma button system
                    (black-pill primary + outline-pill secondary) instead of the
                    one-off .uz-navbar__guest-btn so it matches app buttons (BR-08) */}
                <button
                  type="button"
                  className="btn-black btn-figma-sm"
                  onClick={() => navigate("/signup")}
                >
                  Sign up
                </button>
                <button
                  type="button"
                  className="btn-figma-1 btn-figma-sm"
                  onClick={() => navigate("/signin")}
                >
                  Sign in
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
